import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import FormCheckbox from '../../../components/general/FormCheckbox';
import FormSelect from '../../../components/general/FormSelect';
import FormTextField from '../../../components/general/FormTextField';
import { Button, Collapse } from 'antd';


const { Panel } = Collapse;



const transformEditProduct = (editData, tables) => {
  if (!editData || !editData.ProductDetail) return [];

  return editData.ProductDetail.map((detail) => {
    // Find branches for this detail
    const detailBranches = editData.ProductDetailBranch
      ?.filter(b =>
        b.ProductDetailId === detail.ProductDetailId &&
        b.SizeId === detail.SizeId &&
        b.FlavourId === detail.FlavourId
      )
      .map(b => ({
        BranchId: b.BranchId,
        BranchName: tables.Table6?.find(branch => branch.BranchId === b.BranchId)?.BranchName || '',
        RemoteId: b.RemoteId || ""
      })) || [];

    // Find order sources for this detail
    const detailOrderSources = editData.ProductDetailOrderSource
      ?.filter(os =>
        os.ProductDetailId === detail.ProductDetailId &&
        os.SizeId === detail.SizeId &&
        os.FlavourId === detail.FlavourId
      )
      .map(os => ({
        OrderSourceId: os.OrderSourceId,
        OrderSourceName: tables.Table4?.find(source => source.OrderSourceId === os.OrderSourceId)?.OrderSource || '',
        Price: os.Price || ""
      })) || [];

    // Merge with all available branches (add missing ones)
    const allBranches = tables.Table6?.map(branch => {
      const existingBranch = detailBranches.find(b => b.BranchId === branch.BranchId);
      return existingBranch || {
        BranchId: branch.BranchId,
        BranchName: branch.BranchName,
        RemoteId: ""
      };
    }) || [];

    // Merge with all available order sources (add missing ones)
    const allOrderSources = tables.Table4?.map(source => {
      const existingSource = detailOrderSources.find(os => os.OrderSourceId === source.OrderSourceId);
      return existingSource || {
        OrderSourceId: source.OrderSourceId,
        OrderSourceName: source.OrderSource,
        Price: ""
      };
    }) || [];

    return {
      id: Date.now() + Math.random(),
      ProductDetailId: detail.ProductDetailId,
      SizeId: detail.SizeId?.toString() || "",
      FlavourId: detail.FlavourId?.toString() || "",
      Price: detail.Price?.toString() || "",
      PriceBeforeDiscount: detail.PriceBeforeDiscount?.toString() || "",
      RemoteId: detail.RemoteId || "",
      IsBestSeller: detail.IsBestSeller,
      IsPromotion: detail.IsPromotion,
      IsEnable: detail.IsEnable,
      OnlyForDeal: detail.OnlyForDeal,
      branches: allBranches,
      orderSources: allOrderSources
    };
  });
};





const ProductDetailsManager = ({ supportingTable, onDetailsChange, editProduct = null, isProductUpdate, resetTrigger }) => {



  const initialDetails = useMemo(() => {
    if (isProductUpdate && editProduct) {
      return transformEditProduct(editProduct, supportingTable);
    }
    return [];
  }, [isProductUpdate, editProduct]);

  const [productDetails, setProductDetails] = useState(initialDetails);

  useEffect(() => {
    setProductDetails([]);
  }, [resetTrigger]);


  useEffect(() => {
    if (isProductUpdate && initialDetails.length > 0) {
      setProductDetails(initialDetails);
    }
  }, [isProductUpdate, initialDetails]);



  useEffect(() => {
    onDetailsChange?.(formattedData);
  }, [productDetails]);



  const createEmptyDetail = () => ({
    id: Date.now() + Math.random(),
    ProductDetailId: null,
    SizeId: "",
    FlavourId: "",
    Price: "",
    PriceBeforeDiscount: "",
    RemoteId: "",
    IsBestSeller: false,
    IsPromotion: false,
    IsEnable: true,
    OnlyForDeal: false,
    branches: supportingTable.Table6?.map(branch => ({
      BranchId: branch.BranchId,
      BranchName: branch.BranchName,
      RemoteId: ""
    })) || [],
    orderSources: supportingTable.Table4?.map(source => ({
      OrderSourceId: source.OrderSourceId,
      OrderSourceName: source.OrderSource,
      Price: ""
    })) || []
  });



  const formattedData = useMemo(() => {
    const ProductDetail = productDetails.map(detail => ({
      ProductDetailId: detail.ProductDetailId,
      SizeId: detail.SizeId ? Number(detail.SizeId) : null,
      FlavourId: detail.FlavourId ? Number(detail.FlavourId) : null,
      Price: detail.Price ? Number(detail.Price) : 0,
      PriceBeforeDiscount: detail.PriceBeforeDiscount ? Number(detail.PriceBeforeDiscount) : 0,
      FuturePrice: 0,
      RemoteId: detail.RemoteId || "",
      OnlyForDeal: detail.OnlyForDeal,
      IsBestSeller: detail.IsBestSeller,
      IsPromotion: detail.IsPromotion,
      IsEnable: detail.IsEnable, // ✅ properly included now
    }));

    const ProductDetailBranch = productDetails.flatMap(detail =>
      detail.branches
        .filter(b => b.RemoteId)
        .map(b => ({
          ProductDetailId: detail.ProductDetailId,
          SizeId: detail.SizeId ? Number(detail.SizeId) : null,
          FlavourId: detail.FlavourId ? Number(detail.FlavourId) : null,
          RemoteId: b.RemoteId,
          BranchId: b.BranchId,
        }))
    );

    const ProductDetailOrderSource = productDetails.flatMap(detail =>
      detail.orderSources
        .filter(os => os.Price)
        .map(os => ({
          ProductDetailId: detail.ProductDetailId,
          SizeId: detail.SizeId ? Number(detail.SizeId) : null,
          FlavourId: detail.FlavourId ? Number(detail.FlavourId) : null,
          Price: Number(os.Price),
          OrderSourceId: os.OrderSourceId,
        }))
    );

    return { ProductDetail, ProductDetailBranch, ProductDetailOrderSource };
  }, [productDetails]);

















  const handleAddDetail = () => {
    const newDetails = [...productDetails, createEmptyDetail()];
    setProductDetails(newDetails);
    onDetailsChange?.(formattedData);
  };

  const handleRemoveDetail = (id) => {
    setProductDetails(prev => prev.filter(detail => detail.ProductDetailId !== id));
  };



  const handleDetailChange = (id, fieldData) => {
    const field = fieldData?.name;
    const value =
      typeof fieldData?.checked === "boolean"
        ? fieldData.checked
        : fieldData?.value !== undefined
          ? fieldData.value
          : fieldData;

    const newDetails = productDetails.map(detail =>
      detail.id === id ? { ...detail, [field]: value } : detail
    );

    setProductDetails(newDetails);
  };


  const handleBranchRemoteIdChange = (detailId, branchId, fieldData) => {
    const remoteId = fieldData?.value !== undefined ? fieldData.value : fieldData;

    const newDetails = productDetails.map(detail =>
      detail.id === detailId
        ? {
          ...detail,
          branches: detail.branches.map(branch =>
            branch.BranchId === branchId ? { ...branch, RemoteId: remoteId } : branch
          )
        }
        : detail
    );
    setProductDetails(newDetails);
    setTimeout(() => onDetailsChange?.(formattedData), 0);
  };

  const handleOrderSourcePriceChange = (detailId, sourceId, fieldData) => {
    const price = fieldData?.value !== undefined ? fieldData.value : fieldData;

    const newDetails = productDetails.map(detail =>
      detail.id === detailId
        ? {
          ...detail,
          orderSources: detail.orderSources.map(source =>
            source.OrderSourceId === sourceId ? { ...source, Price: price } : source
          )
        }
        : detail
    );
    setProductDetails(newDetails);
    setTimeout(() => onDetailsChange?.(formattedData), 0);
  };














 return (
    <div className="pt-2 w-full">
      <div className="max-w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Product Details</h2>
          <Button
            onClick={handleAddDetail}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Create Product Detail
          </Button>
        </div>

        {productDetails.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
            No product details added yet. Click "Create Product Detail" to get started.
          </div>
        ) : (
          <Collapse
            accordion
            className="bg-gray-50 rounded-lg"
            defaultActiveKey={[productDetails[productDetails.length - 1]?.id]}
          >
            {productDetails.map((detail, index) => (
              <Panel
                key={detail.id}
                header={
                  <div className="flex justify-between w-full items-center">
                    <span className="text-gray-700 font-medium">
                      Product Detail
                      {/* {supportingTable.Table2?.find(s => s.SizeId == detail.SizeId)?.SizeName || 'N/A'} |  */}
                      {/* {supportingTable.Table3?.find(f => f.FlavourId == detail.FlavourId)?.FlavourName || 'N/A'} */}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveDetail(detail.ProductDetailId);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors bg-red-100"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-1 mb-6">
                  <FormSelect
                    label="Size"
                    name="SizeId"
                    value={detail.SizeId}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                    listItem={supportingTable.Table2}
                    idName="SizeId"
                    valueName="SizeName"
                    required={true}
                  />

                  <FormSelect
                    label="Variant"
                    name="FlavourId"
                    value={detail.FlavourId}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                    listItem={supportingTable.Table3}
                    idName="FlavourId"
                    valueName="FlavourName"
                    required={true}
                  />

                  <FormTextField
                    label="Price"
                    name="Price"
                    type="number"
                    value={detail.Price}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                    required={true}
                  />

                  <FormTextField
                    label="Price Before Discount"
                    name="PriceBeforeDiscount"
                    type="number"
                    value={detail.PriceBeforeDiscount}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />

                  <FormTextField
                    label="Remote Code"
                    name="RemoteId"
                    value={detail.RemoteId}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-1 mb-6">
                  <FormCheckbox
                    label="Cart Suggestion"
                    name="IsPromotion"
                    checked={detail.IsPromotion}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />

                  <FormCheckbox
                    label="Popular Product"
                    name="IsBestSeller"
                    checked={detail.IsBestSeller}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />

                  <FormCheckbox
                    label="Enable"
                    name="IsEnable"
                    checked={detail.IsEnable}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />

                  <FormCheckbox
                    label="Only For Deal"
                    name="OnlyForDeal"
                    checked={detail.OnlyForDeal}
                    onChange={(data) => handleDetailChange(detail.id, data)}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className='border p-4 rounded-xl'>
                    <div className='flex justify-between items-center'>
                      <h4 className="text-md font-semibold text-gray-700 mb-3">Branches</h4>
                      <h4 className="text-md font-semibold text-gray-700 mb-3">Branches Remote Code</h4>
                    </div>
                    <div className="rounded-lg space-y-3">
                      {detail.branches.map((branch) => (
                        <div key={branch.BranchId} className="grid grid-cols-2 gap-3 items-center">
                          <span className="text-sm font-medium text-gray-600">{branch.BranchName}</span>
                          <FormTextField
                            label=""
                            name={`branch_${branch.BranchId}`}
                            value={branch.RemoteId}
                            onChange={(data) => handleBranchRemoteIdChange(detail.id, branch.BranchId, data)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='border p-4 rounded-xl'>
                    <div className='flex justify-between items-center'>
                      <h4 className="text-md font-semibold text-gray-700 mb-3">Order Sources</h4>
                      <h4 className="text-md font-semibold text-gray-700 mb-3">Prices</h4>
                    </div>
                    <div className="rounded-lg space-y-3">
                      {detail.orderSources.map((source) => (
                        <div key={source.OrderSourceId} className="grid grid-cols-2 gap-3 items-center">
                          <span className="text-sm font-medium text-gray-600">{source.OrderSourceName}</span>
                          <FormTextField
                            label=""
                            name={`source_${source.OrderSourceId}`}
                            type="number"
                            value={source.Price}
                            onChange={(data) => handleOrderSourcePriceChange(detail.id, source.OrderSourceId, data)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Panel>
            ))}
          </Collapse>
        )}
      </div>
    </div>
  );
};

export default ProductDetailsManager;